import { Dialog, makeStyles, Typography } from "@material-ui/core"
import { AxiosError } from "axios"
import clsx from "clsx"
import isMobile from "is-mobile"
import QueryString from "query-string"
import { FC, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router"
import { useNavigation } from "../../hooks/useNavigation"
import { useApi } from "../../utils/API"
import { colors } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import Input from "../atoms/input/Input"
import Logo from "../atoms/Logo"
import PasswordRules from "../molecules/PasswordRules"
import { TitledCard } from "../molecules/titledCard/TitledCard"
import { BaseTemplate } from "../templates/BaseTemplate"
import CloseIcon from "../atoms/CloseIcon"

const useStyles = makeStyles((theme) => ({
  logo: {
    position: "absolute",
    top: theme.spacing(6),
    left: theme.spacing(6),
  },
  center: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
  },
  button: {
    width: "100%",
  },
  marginBottom32: {
    marginBottom: theme.spacing(4),
  },
  marginBottom24: {
    marginBottom: theme.spacing(3),
  },
  marginBottom16: {
    marginBottom: theme.spacing(2),
  },
  marginBottom8: {
    marginBottom: theme.spacing(1),
  },
  asphalt: {
    color: colors.neutral.asphalt,
  },
  denim: {
    color: colors.neutral.denim,
  },
  login: {
    marginLeft: -6,
  },
  inputContainer: {
    position: "relative",
    width: "100%",
  },
  input: {
    width: "100%",
  },
  bottomText: {
    color: colors.neutral.asphalt,
    marginRight: 4,
  },
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  },
  closeButton: {
    margin: 0,
    padding: 0,
    border: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  dialogSubtitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
}))

const ResetPassword: FC = () => {
  const classes = useStyles()
  const { resetPassword } = useApi()
  const [password, setPassword] = useState<string>("")
  const [reenterPassword, setReenterPassword] = useState<string>("")
  const [passwordError, setPasswordError] = useState<boolean>(false)
  const [validUrl, setValidUrl] = useState<boolean>(false)
  const [reenterPasswordError, setReenterPasswordError] =
    useState<boolean>(false)
  const { toLogin, toForgotPassword, toResetPasswordSuccess } = useNavigation()
  const location = useLocation()
  const { ticket } = QueryString.parse(location.search)
  const [passwordFocus, setPasswordFocus] = useState<boolean>(false)
  const [reenterPasswordFocus, setReenterPasswordFocus] =
    useState<boolean>(false)
  const [validPassword, setValidPassword] = useState<boolean>(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )

  useEffect(() => {
    if (validUrl) {
      return
    }
    if (ticket && ticket.length > 0) {
      setValidUrl(true)
    }
  }, [ticket, validUrl])

  /*
   * The hook below pops up an "open in app" when you use a mobile device to access
   * this webpage. Worth reimplementing when we decide to get password reset in app working
   * again.
   */
  // useEffect(() => {
  //   if (isMobile() && ticket && ticket.length > 0 && buttonRef) {
  //     const link = document.createElement("a")
  //     link.href = `vira://reset-password/${ticket}`
  //     document.body.appendChild(link)
  //     link.click()
  //     document.body.removeChild(link)
  //   }
  // }, [ticket])

  const onSubmit = async () => {
    setPasswordError(false)
    setReenterPasswordError(false)
    if (!password) {
      setPasswordError(true)
      return
    }
    if (!reenterPassword) {
      setReenterPasswordError(true)
      return
    }
    if (!validPassword) {
      setPasswordError(true)
      setReenterPasswordError(true)
      return
    }
    if (!ticket) {
      return
    }
    try {
      await resetPassword(ticket as string, password)
      toResetPasswordSuccess()
    } catch (e) {
      if (e instanceof Error && e.hasOwnProperty("response")) {
        const error = e as AxiosError
        if (error.response?.data) {
          const data = error.response.data as {
            statusCode?: number
            message?: string
          }
          setErrorMessage(
            data.statusCode === 401
              ? "This link has expired or already been used."
              : data.message
          )
        } else {
          setErrorMessage("An unexpected error occurred. Please try again.")
        }
      }
    }
  }

  return (
    <BaseTemplate>
      <Logo className={classes.logo} />
      {validUrl ? (
        <TitledCard title="Reset Password">
          <Typography
            variant="body1"
            className={clsx(classes.marginBottom32, classes.denim)}
          >
            Please create a new password.
          </Typography>
          <div className={classes.inputContainer}>
            <Input
              onFocus={() => setPasswordFocus(true)}
              onBlur={() => setPasswordFocus(false)}
              focused={passwordFocus}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={passwordError}
              className={clsx(classes.marginBottom24, classes.input)}
              label="Password"
              placeholder="Password"
              type="password"
              enableShowPassword={true}
            />
            {passwordFocus && (
              <PasswordRules
                password={password}
                comparisonPassword={reenterPassword}
                setValidPassword={setValidPassword}
              />
            )}
          </div>
          <div className={classes.inputContainer}>
            <Input
              onFocus={() => setReenterPasswordFocus(true)}
              onBlur={() => setReenterPasswordFocus(false)}
              focused={reenterPasswordFocus}
              value={reenterPassword}
              onChange={(event) => setReenterPassword(event.target.value)}
              error={reenterPasswordError}
              className={clsx(classes.marginBottom24, classes.input)}
              label="Re-enter password"
              placeholder="Re-enter password"
              type="password"
              enableShowPassword={true}
            />
            {reenterPasswordFocus && (
              <PasswordRules
                password={password}
                comparisonPassword={reenterPassword}
                setValidPassword={setValidPassword}
                compare={true}
              />
            )}
          </div>
          <div className={classes.marginBottom16}>
            <PrimaryButton className={classes.button} onClick={onSubmit}>
              Submit
            </PrimaryButton>
          </div>
          <div className={clsx(classes.center, classes.marginBottom8)}>
            <Typography variant="h6" className={classes.bottomText}>
              Return to
            </Typography>
            <GhostButton onClick={toLogin}>Log In</GhostButton>
          </div>
        </TitledCard>
      ) : (
        <TitledCard title="Reset Password">
          {!isMobile() && (
            <Typography
              variant="body1"
              className={clsx(classes.marginBottom32, classes.denim)}
            >
              Sorry, the link you followed is not valid.
            </Typography>
          )}
          <PrimaryButton
            ref={buttonRef}
            className={classes.button}
            onClick={
              !isMobile()
                ? toForgotPassword
                : () => window.open(`vira://reset-password/${ticket}`, "_blank")
            }
          >
            {!isMobile() ? "Try Again" : "Click here to open the app"}
          </PrimaryButton>
        </TitledCard>
      )}
      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={!!errorMessage}
        onClose={() => setErrorMessage(undefined)}
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogContent}>
          <div className={classes.dialogCloseContainer}>
            <button
              className={classes.closeButton}
              onClick={() => setErrorMessage(undefined)}
            >
              <CloseIcon />
            </button>
          </div>
          <Typography variant="h2" className={classes.dialogTitle}>
            Error
          </Typography>
          <Typography variant="body1" className={classes.dialogSubtitle}>
            {errorMessage}
          </Typography>
          {!isMobile() && (
            <PrimaryButton className={classes.button} onClick={toLogin}>
              Return to Login
            </PrimaryButton>
          )}
        </div>
      </Dialog>
    </BaseTemplate>
  )
}

export default ResetPassword
